import { useCallback } from 'react';
import { getConsentPreferences } from '../utils/consent';
import { safeGtag, waitForGtag, logGtagDebugInfo } from '../utils/gtag-debug';

// GA4 Event types
export interface GA4Event {
  event_name: string;
  event_category?: string;
  event_label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

// Common GA4 events
export const GA4_EVENTS = {
  // Page views
  PAGE_VIEW: 'page_view',
  
  // User interactions
  CLICK: 'click',
  FORM_SUBMIT: 'form_submit',
  SCROLL: 'scroll',
  
  // Business events
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  PHONE_CLICK: 'phone_click',
  EMAIL_CLICK: 'email_click',
  APPOINTMENT_REQUEST: 'appointment_request',
  
  // Content engagement
  VIDEO_PLAY: 'video_play',
  VIDEO_COMPLETE: 'video_complete',
  FILE_DOWNLOAD: 'file_download',
  
  // Navigation
  MENU_CLICK: 'menu_click',
  LANGUAGE_SWITCH: 'language_switch',
  
  // Consent
  CONSENT_GRANTED: 'consent_granted',
  CONSENT_DENIED: 'consent_denied',
} as const;

export function useGA4() {
  // Track a custom event
  const trackEvent = useCallback(async (eventName: string, parameters?: Record<string, any>) => {
    // Check if analytics consent is granted
    const consent = getConsentPreferences();
    if (!consent?.analytics) {
      console.log('GA4 tracking skipped - no analytics consent');
      return;
    }

    // Wait for gtag to be available (with timeout)
    const gtagAvailable = await waitForGtag(3000);
    
    if (!gtagAvailable) {
      console.warn('GA4 gtag not available after waiting');
      if (process.env.NODE_ENV === 'development') {
        logGtagDebugInfo();
      }
      return;
    }

    // Use safe gtag function
    const success = safeGtag('event', eventName, {
      ...parameters,
      // Add timestamp for debugging
      timestamp: new Date().toISOString(),
    });
    
    if (success) {
      console.log('GA4 Event tracked:', eventName, parameters);
    } else {
      console.error('Failed to track GA4 event:', eventName, parameters);
    }
  }, []);

  // Track page view
  const trackPageView = useCallback((page_title?: string, page_location?: string) => {
    trackEvent(GA4_EVENTS.PAGE_VIEW, {
      page_title: page_title || document.title,
      page_location: page_location || window.location.href,
    });
  }, [trackEvent]);

  // Track form submission
  const trackFormSubmit = useCallback((formName: string, success: boolean = true) => {
    trackEvent(GA4_EVENTS.FORM_SUBMIT, {
      form_name: formName,
      success,
    });
  }, [trackEvent]);

  // Track contact form submission
  const trackContactForm = useCallback((method: 'form' | 'phone' | 'email', success: boolean = true) => {
    trackEvent(GA4_EVENTS.CONTACT_FORM_SUBMIT, {
      contact_method: method,
      success,
    });
  }, [trackEvent]);

  // Track phone click
  const trackPhoneClick = useCallback((phoneNumber: string) => {
    trackEvent(GA4_EVENTS.PHONE_CLICK, {
      phone_number: phoneNumber,
    });
  }, [trackEvent]);

  // Track email click
  const trackEmailClick = useCallback((emailAddress: string) => {
    trackEvent(GA4_EVENTS.EMAIL_CLICK, {
      email_address: emailAddress,
    });
  }, [trackEvent]);

  // Track language switch
  const trackLanguageSwitch = useCallback((fromLanguage: string, toLanguage: string) => {
    trackEvent(GA4_EVENTS.LANGUAGE_SWITCH, {
      from_language: fromLanguage,
      to_language: toLanguage,
    });
  }, [trackEvent]);

  // Track consent decisions
  const trackConsentDecision = useCallback((decision: 'granted' | 'denied', categories: string[]) => {
    const eventName = decision === 'granted' ? GA4_EVENTS.CONSENT_GRANTED : GA4_EVENTS.CONSENT_DENIED;
    trackEvent(eventName, {
      consent_categories: categories.join(','),
    });
  }, [trackEvent]);

  // Track menu interactions
  const trackMenuClick = useCallback((menuItem: string, menuType: 'main' | 'mobile' | 'footer' = 'main') => {
    trackEvent(GA4_EVENTS.MENU_CLICK, {
      menu_item: menuItem,
      menu_type: menuType,
    });
  }, [trackEvent]);

  // Track scroll depth
  const trackScrollDepth = useCallback((depth: number) => {
    trackEvent(GA4_EVENTS.SCROLL, {
      scroll_depth: depth,
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackFormSubmit,
    trackContactForm,
    trackPhoneClick,
    trackEmailClick,
    trackLanguageSwitch,
    trackConsentDecision,
    trackMenuClick,
    trackScrollDepth,
  };
}
